import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Cookie as CookieIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { getCookieInfo } from '../utils/cookieConsent';

const CookiePolicy = () => {
  const cookieInfo = getCookieInfo();
  const lastUpdated = "20 August 2025";

  const handleOpenSettings = () => {
    // Trigger cookie settings modal
    window.dispatchEvent(new CustomEvent('openCookieSettings'));
  };

  const cookieDetails = [
    {
      name: 'Session Cookies',
      purpose: 'Essential functionality and security',
      duration: 'Session only',
      type: 'Necessary',
      examples: 'User authentication, form data'
    },
    {
      name: 'Google Analytics (_ga, _gid)',
      purpose: 'Website analytics and performance monitoring',
      duration: '2 years / 24 hours',
      type: 'Analytics',
      examples: 'Page views, user behavior, site performance'
    },
    {
      name: 'Cookie Consent',
      purpose: 'Remember your cookie preferences',
      duration: '1 year',
      type: 'Necessary',
      examples: 'Your consent choices, preference settings'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <CookieIcon color="primary" sx={{ fontSize: 40 }} />
          <Typography variant="h3" component="h1" sx={{ fontWeight: 600 }}>
            Cookie Policy
          </Typography>
        </Box>
        
        <Typography variant="body1" color="text.secondary" paragraph>
          Last updated: {lastUpdated}
        </Typography>

        <Box sx={{ mb: 4, p: 3, backgroundColor: 'primary.light', borderRadius: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This Cookie Policy explains how Salary Tools ("we", "us", or "our") uses cookies 
            and similar technologies when you visit our website.
          </Typography>
          <Button
            variant="contained"
            startIcon={<SettingsIcon />}
            onClick={handleOpenSettings}
            sx={{ fontWeight: 600 }}
          >
            Manage Cookie Preferences
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
          What Are Cookies?
        </Typography>
        <Typography variant="body1" paragraph>
          Cookies are small text files that are stored on your device when you visit a website. 
          They help us provide you with a better experience by remembering your preferences, 
          analyzing how you use our site, and enabling certain functionality.
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
          Types of Cookies We Use
        </Typography>
        
        {Object.entries(cookieInfo).map(([categoryKey, categoryData]) => (
          <Accordion key={categoryKey} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {categoryData.name}
                </Typography>
                <Chip
                  label={categoryData.canDisable ? 'Optional' : 'Required'}
                  size="small"
                  color={categoryData.canDisable ? 'default' : 'primary'}
                  variant="outlined"
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body1" paragraph>
                {categoryData.description}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Examples:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {categoryData.examples.map((example, index) => (
                  <Chip
                    key={index}
                    label={example}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.75rem' }}
                  />
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
          Cookie Details
        </Typography>
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Cookie Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Purpose</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cookieDetails.map((cookie, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                    {cookie.name}
                  </TableCell>
                  <TableCell>{cookie.purpose}</TableCell>
                  <TableCell>{cookie.duration}</TableCell>
                  <TableCell>
                    <Chip
                      label={cookie.type}
                      size="small"
                      color={cookie.type === 'Necessary' ? 'primary' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
          Your Cookie Choices
        </Typography>
        <Typography variant="body1" paragraph>
          You have several options for managing cookies:
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" variant="body1" paragraph>
            <strong>Cookie Banner:</strong> When you first visit our site, you can choose to 
            accept all cookies, accept only necessary cookies, or customize your preferences.
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Cookie Settings:</strong> You can change your preferences at any time by 
            clicking the "Manage Cookie Preferences" button above or in our website footer.
          </Typography>
          <Typography component="li" variant="body1" paragraph>
            <strong>Browser Settings:</strong> You can configure your browser to block or 
            delete cookies, though this may affect site functionality.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
          Third-Party Cookies
        </Typography>
        <Typography variant="body1" paragraph>
          We use Google Analytics to understand how visitors interact with our website. 
          Google Analytics uses cookies to collect information anonymously and report 
          website trends without identifying individual visitors.
        </Typography>
        <Typography variant="body1" paragraph>
          For more information about Google Analytics cookies, please visit: 
          <Typography
            component="a"
            href="https://developers.google.com/analytics/devguides/collection/analyticsjs/cookie-usage"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ ml: 1, color: 'primary.main' }}
          >
            Google Analytics Cookie Usage
          </Typography>
        </Typography>
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
          Updates to This Policy
        </Typography>
        <Typography variant="body1" paragraph>
          We may update this Cookie Policy from time to time to reflect changes in our 
          practices or for legal, operational, or regulatory reasons. We will notify you 
          of any material changes by posting the updated policy on our website.
        </Typography>
      </Box>

      <Paper sx={{ p: 3, backgroundColor: 'grey.50' }}>
        <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
          Contact Us
        </Typography>
        <Typography variant="body1" paragraph>
          If you have any questions about this Cookie Policy or our use of cookies, 
          please contact us through our website or privacy policy page.
        </Typography>
        <Button
          variant="outlined"
          onClick={handleOpenSettings}
          startIcon={<SettingsIcon />}
          sx={{ mt: 1 }}
        >
          Manage Cookie Settings
        </Button>
      </Paper>
    </Container>
  );
};

export default CookiePolicy;